import { LanguagesService, TranslateConfigPipe, minLessThanMaxValidator } from '@alpha-core/platform-ui';
import { Component, EventEmitter, HostListener, Input, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import { AlphaSharedModule } from '../../../shared/alpha-shared.module';
import { FilterDateRangeComponent } from './filter-date-range/filter-date-range.component';
import { FilterEnumComponent } from './filter-enum/filter-enum.component';
import { FilterNumberRangeComponent } from './filter-number-range/filter-number-range.component';
import { FilterSearchRangeComponent } from './filter-search-range/filter-search-range.component';
@Component({
    selector: 'app-inbox-filters',
    standalone: true,
    templateUrl: './inbox-filters.component.html',
    styleUrl: './inbox-filters.component.css',
    imports: [
        FilterDateRangeComponent,
        FilterSearchRangeComponent,
        FilterEnumComponent,
        FilterNumberRangeComponent,
        ReactiveFormsModule,
        AlphaSharedModule,
        TranslateConfigPipe,
    ],
})
export class InboxFiltersComponent {
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private languageService = inject(LanguagesService);

    @Input() formValue!: any;
    @Input() filterArray!: any;
    filterMenu: boolean = false;
    applyFilterArray: any[] = [];
    @Output() applyFilterChange = new EventEmitter<any>();
    @Output() onClearAllFilters = new EventEmitter<any>();
    myForm!: FormGroup;
    @ViewChild('op') filteroverlayPanel!: OverlayPanel;
    formSubscription!: Subscription;
    hideDateRange: boolean = false;
    ngOnChanges(__changes: SimpleChanges) {
        if (__changes['formValue'] || __changes['filterArray']) {
            this.setFilterForm(this.formValue || {});
            this.formSubscription?.unsubscribe();
            this.formSubscription = this.myForm.statusChanges.subscribe(() => {
                if (this.filteroverlayPanel) {
                    this.filteroverlayPanel.unbindDocumentClickListener();
                    this.filteroverlayPanel.dismissable = this.myForm.valid;
                    this.filteroverlayPanel.bindDocumentClickListener();
                }
            });
        }
    }

    setFilterForm(formValue: any) {
        const formGroupConfig: any = {};
        this.filterArray?.filters.forEach((filter: any) => {
            filter.fields.forEach((field: any) => {
                if (field.filterOptions.type === 'enum' || field.filterOptions.type === 'boolean') {
                    let enumConfig: any = {
                        type: new FormControl(field.filterOptions.type),
                        mapping: new FormControl(field.mapping),
                        mappingType: new FormControl(field.mappingType),
                        _exactMatch: new FormControl(field.filterOptions?._exactMatch || false),
                    };
                    field.filterOptions.options.forEach((option: string) => {
                        enumConfig[option] = new FormControl(false);
                    });
                    enumConfig.label = new FormControl(field.label);
                    formGroupConfig[field.name] = new FormGroup(enumConfig);
                } else if (field.filterOptions.type === 'number-range') {
                    let rangeConfig: any = {
                        type: new FormControl('number-range'),
                        mapping: new FormControl(field.mapping),
                        mappingType: new FormControl(field.mappingType),
                    };
                    rangeConfig['minLabel'] = new FormControl('', [Validators.pattern(/^[1-9]\d*$/)]);
                    rangeConfig['maxLabel'] = new FormControl('', [Validators.pattern(/^[1-9]\d*$/)]);
                    rangeConfig['label'] = new FormControl(field.label);
                    formGroupConfig[field.name] = new FormGroup(rangeConfig, { validators: minLessThanMaxValidator() });
                } else if (field.filterOptions.type === 'date-range') {
                    let rangeConfig: any = {
                        type: new FormControl(field.filterOptions.type),
                        mapping: new FormControl(field.mapping),
                        mappingType: new FormControl(field.mappingType),
                        value: new FormControl(null, this.customDateRangeValidator()),
                    };
                    rangeConfig.label = new FormControl(field.label);
                    formGroupConfig[field.name] = new FormGroup(rangeConfig);
                } else {
                    let rangeConfig: any = {
                        type: new FormControl(field.filterOptions.type),
                        mapping: new FormControl(field.mapping),
                        mappingType: new FormControl(field.mappingType),
                        _exactMatch: new FormControl(field.filterOptions?._exactMatch || false),
                        value: new FormControl(null),
                    };
                    rangeConfig.label = new FormControl(field.label);
                    formGroupConfig[field.name] = new FormGroup(rangeConfig);
                }
            });
        });

        this.myForm = this.fb.group(formGroupConfig);
        this.myForm.patchValue(formValue);
    }

    customDateRangeValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: string[] | null = control.value;
            if (Array.isArray(value)) {
                const [startDate, endDate] = value;
                // Check if any of the dates are null or empty
                if (!startDate || !endDate) {
                    return { invalid: true };
                }
            }
            // If all dates are present, return null (valid)
            return null;
        };
    }

    minLessThanMaxValidator(): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const min = Number(group.get('minLabel')?.value);
            const max = Number(group.get('maxLabel')?.value);

            if (isNaN(min) || isNaN(max)) {
                return null;
            }

            return min > max ? { minGreaterThanMax: true } : null;
        };
    }

    onOverlayPanelHide() {
        this.filterMenu = !this.filterMenu;
        this.hideDateRange = false;
    }

    onOverlayPanelShow() {
        this.filterMenu = !this.filterMenu;
        this.filteroverlayPanel.onEscapeKeydown = () => {};
        this.hideDateRange = false;
    }

    //apply filter to the inbox

    applyFilter(overlayPanel: OverlayPanel) {
        if (this.myForm.valid) {
            this.applyFilterChange.emit(this.myForm.value);
            overlayPanel?.hide();
        } else console.error('Filter is not valid');
    }

    onCancel(overlayPanel: OverlayPanel) {
        this.setFilterForm({});
        this.onClearAllFilters.emit();
    }

    hideOverlayPanel() {
        if (!this.myForm.valid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Invalid filter',
                detail: 'Please check the entered values and correct the errors before submitting.',
            });
            return;
        }
        this.filteroverlayPanel.hide();
    }

    getFormValues() {
        return this.myForm.value;
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleEscapeKeyEvent(event: KeyboardEvent) {
        if (!this.myForm.valid) {
            if (this.filteroverlayPanel) {
                event.preventDefault();
                event.stopPropagation();
                this.filteroverlayPanel.overlayVisible = true;
            }
        } else if (this.filteroverlayPanel && this.filteroverlayPanel.overlayVisible) {
            this.hideOverlayPanel();
            event.stopPropagation();
        }
    }

    ngOnDestroy() {
        this.formSubscription?.unsubscribe();
    }
}
